import { createContext, useContext } from "react";
import type { SubCallTrace, BridgeStepTrace, TraceError } from "../../lib/traceApi";

// TraceFilterContext threads a tree-wide filter query through StageRow →
// LayerRow → SubCallRow / BridgeStepRow / PerItemGroupRow without prop-
// drilling. When the query is non-empty, rows whose searchable text
// (sub_prompt_id / item_key / step_id / error code / error detail) does
// NOT contain the query (case-insensitive) hide themselves. Parent rows
// stay visible if ANY descendant matches so the path to the match is
// preserved. (Item 2 of the trace-view refactor.)
//
// Default value is an empty query → no filtering, original behavior.
export interface TraceFilterState {
  query: string;
}

export const TraceFilterContext = createContext<TraceFilterState>({
  query: "",
});

export function useTraceFilter(): TraceFilterState {
  return useContext(TraceFilterContext);
}

// matchesQuery is the canonical predicate every trace-tree row uses to
// decide whether to render itself. Returns true when the query is empty
// (no filtering active) OR when at least one searchable string contains
// the query as a substring.
export function matchesQuery(query: string, searchable: string[]): boolean {
  if (query === "") return true;
  const q = query.toLowerCase();
  return searchable.some((s) => s.toLowerCase().includes(q));
}

// subCallSearchable extracts the strings a SubCallRow exposes to the
// filter — sub_prompt_id, item_key, plus any attempt feedback/error
// (so "Codex" or "context canceled" or "schema validation" filters work).
export function subCallSearchable(sc: SubCallTrace): string[] {
  const out: string[] = [sc.sub_prompt_id, sc.item_key, sc.outcome];
  for (const a of sc.attempts ?? []) {
    if (a.feedback) out.push(a.feedback);
    if (a.error) out.push(a.error);
  }
  return out;
}

export function bridgeStepSearchable(step: BridgeStepTrace): string[] {
  return [step.step_id, step.description, step.error].filter(Boolean);
}

export function errorSearchable(e: TraceError): string[] {
  return [e.stage, e.source, e.code, e.detail].filter(Boolean);
}
